import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import { api } from '@/lib/api';
import { env } from '@/lib/env';
import { X, Camera, Image as ImageIcon } from 'lucide-react';

type Step = 'choose' | 'review' | 'sending' | 'done' | 'error';

const TYPE_OPTIONS = ['auto', 'DRAWING', 'SIENA_WRITING', 'LETTER', 'INVITE', 'CARD', 'CERTIFICATE', 'FLYER', 'OTHER'] as const;

export function CaptureFlow() {
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('choose');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [hintType, setHintType] = useState<(typeof TYPE_OPTIONS)[number]>('auto');
  const [progress, setProgress] = useState<string>('');
  const [errMsg, setErrMsg] = useState<string>('');

  const pick = () => fileInput.current?.click();

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setStep('review');
  };

  const send = async () => {
    if (!file) return;
    setStep('sending');
    try {
      setProgress('Compressing…');
      const processed = await imageCompression(file, {
        maxWidthOrHeight: 2048,
        maxSizeMB: 1.5,
        useWebWorker: true,
        initialQuality: 0.85,
        fileType: 'image/jpeg',
      });

      const captureId = uuidv4();
      if (env.mock) {
        setProgress('Uploading…');
        await new Promise((r) => setTimeout(r, 500));
        setProgress('Reading…');
        await new Promise((r) => setTimeout(r, 800));
        setProgress('Pinning…');
        // Use the in-browser blob URL so the mock capture shows the actual photo.
        await api.finaliseCapture(
          captureId,
          hintType === 'auto' ? 'DRAWING' : hintType,
          URL.createObjectURL(processed),
        );
        setStep('done');
        return;
      }

      setProgress('Uploading…');
      const { uploadUrl } = await api.createCapture(captureId, 'image/jpeg', processed.size);
      await fetch(uploadUrl, {
        method: 'PUT',
        body: processed,
        headers: { 'Content-Type': 'image/jpeg' },
      });
      setProgress('Reading…');
      await api.finaliseCapture(captureId, hintType === 'auto' ? undefined : hintType);
      setProgress('Pinning…');
      setStep('done');
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : String(e));
      setStep('error');
    }
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <header className="flex items-center justify-between p-5 border-b border-black/5">
        <h1 className="font-serif italic text-display-sm">Capture</h1>
        <button onClick={() => navigate(-1)} aria-label="Close">
          <X />
        </button>
      </header>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPick}
      />

      <main className="flex-1 p-5 flex flex-col gap-5 max-w-md mx-auto w-full">
        {step === 'choose' && (
          <>
            <button
              onClick={pick}
              className="w-full py-12 rounded-lg text-white text-body-lg font-medium flex flex-col items-center gap-2 shadow"
              style={{ background: 'linear-gradient(135deg, #E94D2C 0%, #B83518 100%)' }}
            >
              <Camera size={32} /> Take a photo
            </button>
            <button
              onClick={pick}
              className="w-full py-8 rounded-lg border border-black/10 text-ink text-body-lg flex flex-col items-center gap-2 bg-card"
            >
              <ImageIcon size={28} /> Choose from library
            </button>
          </>
        )}

        {step === 'review' && preview && (
          <>
            <img src={preview} alt="preview" className="rounded-md shadow w-full" />
            <label className="flex flex-col gap-1 text-body-sm text-inkSoft">
              What is this?
              <select
                value={hintType}
                onChange={(e) => setHintType(e.target.value as (typeof TYPE_OPTIONS)[number])}
                className="rounded-sm border border-black/10 p-2 bg-card"
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t === 'auto' ? 'Auto-detect' : t.toLowerCase()}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex gap-3 mt-auto">
              <button
                onClick={() => {
                  setFile(null);
                  setPreview(null);
                  setStep('choose');
                }}
                className="flex-1 py-3 rounded-md border border-black/10 bg-card"
              >
                Cancel
              </button>
              <button
                onClick={send}
                className="flex-1 py-3 rounded-md text-white shadow"
                style={{ background: 'linear-gradient(135deg, #E94D2C 0%, #B83518 100%)' }}
              >
                Send to the wall
              </button>
            </div>
          </>
        )}

        {step === 'sending' && (
          <div className="flex flex-col items-center justify-center gap-4 mt-12">
            <div className="w-48 h-1 bg-black/10 rounded-full overflow-hidden">
              <div className="h-full bg-tomato animate-pulse" style={{ width: '60%' }} />
            </div>
            <p className="text-inkFaded">{progress}</p>
          </div>
        )}

        {step === 'done' && (
          <div className="flex flex-col items-center gap-3 mt-12">
            <p className="font-serif italic text-display-sm">Pinned to the wall.</p>
            <button onClick={() => navigate('/wall')} className="text-tomato underline">
              Back to the wall
            </button>
          </div>
        )}

        {step === 'error' && (
          <div className="flex flex-col items-center gap-3 mt-12">
            <p className="font-serif italic text-display-sm text-tomatoDeep">Couldn't send that.</p>
            <p className="text-body-sm text-inkFaded text-center">{errMsg}</p>
            <button onClick={() => setStep('review')} className="text-tomato underline">
              Try again
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
